import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  LogOut,
  Film,
  Inbox,
  TrendingUp,
  CheckCircle2,
  XCircle,
  Phone,
  ExternalLink,
  Trash2,
  Loader2,
} from "lucide-react";

const STATUS_LABELS = {
  new: { label: "Nuovo", color: "bg-gold text-graphite-950" },
  contacted: { label: "Contattato", color: "bg-blue-500/20 text-blue-300 border border-blue-500/40" },
  won: { label: "Vinto", color: "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40" },
  lost: { label: "Perso", color: "bg-red-500/20 text-red-300 border border-red-500/40" },
};

const FILTERS = [
  { key: "all", label: "Tutti" },
  { key: "new", label: "Nuovi" },
  { key: "contacted", label: "Contattati" },
  { key: "won", label: "Vinti" },
  { key: "lost", label: "Persi" },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, q] = await Promise.all([
        api.get("/admin/stats"),
        api.get(`/admin/quotes${filter !== "all" ? `?status=${filter}` : ""}`),
      ]);
      setStats(s.data);
      setQuotes(q.data);
    } catch (e) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/quotes/${id}`, { status });
      toast.success("Stato aggiornato");
      setSelected((s) => (s && s.id === id ? { ...s, status } : s));
      load();
    } catch {
      toast.error("Errore aggiornamento");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa richiesta?")) return;
    try {
      await api.delete(`/admin/quotes/${id}`);
      toast.success("Eliminato");
      if (selected?.id === id) setSelected(null);
      load();
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-graphite-950 text-white">
      {/* Top bar */}
      <header className="sticky top-0 z-40 glass border-b border-white/10">
        <div className="container-x flex items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2">
              <Film strokeWidth={1.5} className="w-5 h-5 text-gold" />
              <span className="font-display text-lg">
                Falco<span className="text-gold"> Poulo</span>
              </span>
            </Link>
            <span className="hidden md:inline text-white/30 mx-2">/</span>
            <span className="hidden md:inline text-sm text-white/60">Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-sm text-white/60" data-testid="admin-user-email">
              {user?.email}
            </span>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="btn-ghost text-sm py-2"
            >
              <LogOut strokeWidth={1.5} className="w-4 h-4" /> Esci
            </button>
          </div>
        </div>
      </header>

      <div className="container-x py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
          <StatCard label="Preventivi totali" value={stats?.total_quotes ?? 0} icon={Inbox} testid="stat-total" />
          <StatCard label="Nuovi" value={stats?.new ?? 0} icon={Inbox} accent testid="stat-new" />
          <StatCard label="Contattati" value={stats?.contacted ?? 0} icon={Phone} testid="stat-contacted" />
          <StatCard label="Vinti" value={stats?.won ?? 0} icon={CheckCircle2} testid="stat-won" />
          <StatCard label="Fatturato stimato" value={`€${stats?.revenue_estimate ?? 0}`} icon={TrendingUp} testid="stat-revenue" />
        </div>

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`admin-filter-${f.key}`}
              className={`px-4 py-2 text-xs uppercase tracking-widest rounded-full border transition-all ${
                filter === f.key
                  ? "bg-gold text-graphite-950 border-gold"
                  : "border-white/15 text-white/60 hover:border-white/40 hover:text-white"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Quotes list */}
          <div className="lg:col-span-3 border border-white/10 rounded-xl bg-graphite-900/40 overflow-hidden">
            <div className="p-4 border-b border-white/10 flex justify-between items-center">
              <h2 className="font-display text-lg">Richieste preventivo</h2>
              {loading && <Loader2 strokeWidth={1.5} className="w-4 h-4 animate-spin text-white/40" />}
            </div>
            <div className="divide-y divide-white/10 max-h-[70vh] overflow-y-auto">
              {quotes.length === 0 && !loading && (
                <div className="p-12 text-center text-white/40 text-sm" data-testid="admin-empty-state">
                  Nessuna richiesta in questa categoria.
                </div>
              )}
              {quotes.map((q) => (
                <button
                  key={q.id}
                  onClick={() => setSelected(q)}
                  data-testid={`quote-row-${q.id}`}
                  className={`w-full text-left p-5 hover:bg-white/[0.03] transition-colors ${
                    selected?.id === q.id ? "bg-white/[0.05]" : ""
                  }`}
                >
                  <div className="flex justify-between items-start gap-4 mb-2">
                    <div>
                      <div className="font-medium">{q.name}</div>
                      <div className="text-xs text-white/50">{q.email}</div>
                    </div>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${STATUS_LABELS[q.status]?.color || ""}`}>
                      {STATUS_LABELS[q.status]?.label || q.status}
                    </span>
                  </div>
                  <div className="flex gap-3 text-xs text-white/50 mb-2">
                    <span>{q.project_type}</span>
                    {q.budget && <span>· {q.budget}</span>}
                    {q.city && <span>· {q.city}</span>}
                  </div>
                  <p className="text-sm text-white/70 line-clamp-2">{q.message}</p>
                  <div className="text-[10px] text-white/30 mt-2">
                    {new Date(q.created_at).toLocaleString("it-IT")}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Detail panel */}
          <div className="lg:col-span-2">
            <div className="lg:sticky lg:top-24 border border-white/10 rounded-xl bg-graphite-900/40 p-6">
              {!selected ? (
                <div className="text-center py-16 text-white/40 text-sm">
                  Seleziona una richiesta per vedere i dettagli.
                </div>
              ) : (
                <div data-testid="quote-detail-panel">
                  <div className="flex justify-between items-start gap-4 mb-6">
                    <div>
                      <h3 className="font-display text-2xl font-medium">{selected.name}</h3>
                      <div className="text-sm text-white/50 mt-1">
                        {new Date(selected.created_at).toLocaleString("it-IT")}
                      </div>
                    </div>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${STATUS_LABELS[selected.status]?.color || ""}`}>
                      {STATUS_LABELS[selected.status]?.label}
                    </span>
                  </div>

                  <dl className="space-y-3 text-sm mb-6">
                    <Row k="Email" v={<a href={`mailto:${selected.email}`} className="text-gold hover:underline">{selected.email}</a>} />
                    {selected.phone && <Row k="Telefono" v={<a href={`tel:${selected.phone}`} className="text-gold hover:underline">{selected.phone}</a>} />}
                    {selected.business && <Row k="Attività" v={selected.business} />}
                    {selected.city && <Row k="Città" v={selected.city} />}
                    <Row k="Tipo" v={selected.project_type} />
                    {selected.budget && <Row k="Budget" v={selected.budget} />}
                  </dl>

                  <div className="border-t border-white/10 pt-4 mb-6">
                    <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Messaggio</div>
                    <p className="text-white/85 leading-relaxed whitespace-pre-line">{selected.message}</p>
                  </div>

                  <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Cambia stato</div>
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {["new", "contacted", "won", "lost"].map((s) => (
                      <button
                        key={s}
                        onClick={() => updateStatus(selected.id, s)}
                        data-testid={`status-btn-${s}`}
                        className={`px-3 py-2 text-xs rounded-md border transition-all ${
                          selected.status === s
                            ? "border-gold bg-gold/10 text-gold"
                            : "border-white/15 text-white/70 hover:border-white/40"
                        }`}
                      >
                        {STATUS_LABELS[s].label}
                      </button>
                    ))}
                  </div>

                  <div className="flex gap-2">
                    <a
                      href={`mailto:${selected.email}?subject=Preventivo Falco Poulo&body=Ciao ${selected.name},%0D%0A%0D%0AGrazie per la tua richiesta...`}
                      data-testid="quote-reply-btn"
                      className="btn-ghost text-sm flex-1 justify-center"
                    >
                      <ExternalLink strokeWidth={1.5} className="w-4 h-4" /> Rispondi
                    </a>
                    <button
                      onClick={() => remove(selected.id)}
                      data-testid="quote-delete-btn"
                      className="btn-ghost text-sm text-red-400 hover:text-red-300 border-red-500/20 hover:border-red-500/40"
                    >
                      <Trash2 strokeWidth={1.5} className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className={`p-5 rounded-xl border ${
        accent ? "border-gold/40 bg-gold/5" : "border-white/10 bg-graphite-900/60"
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] uppercase tracking-widest text-white/50">{label}</span>
        <Icon strokeWidth={1.5} className={`w-4 h-4 ${accent ? "text-gold" : "text-white/40"}`} />
      </div>
      <div className={`font-display text-3xl font-medium ${accent ? "text-gold" : ""}`}>{value}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-white/50 text-xs uppercase tracking-widest">{k}</dt>
      <dd className="text-white text-right break-all">{v}</dd>
    </div>
  );
}
